import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ToastQueue } from "@react-spectrum/toast";
import {
    TreeGridColumn,
    TreeGridRow,
} from "../../../../../components/ui/TreeGridComponent/types";
import {
    TreeGridComponent,
} from "../../../../../components/ui/TreeGridComponent/TreeGridComponent";

import styles from "./invoice_transaction_section.module.css";
import { Button } from "@adobe/react-spectrum";

interface InvoiceTransactionComponentProps {
    invoice: any;
    isEdit: boolean;
    onInvoiceChange: (invoice: any) => void;
}

type Txn = any;

const toNum = (v: any) => {
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;
    const s = String(v ?? "").trim();
    if (!s) return 0;
    // allow both "1,23" and "1.23"
    const n = parseFloat(s.replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

const formatMoney = (amount: any, currency?: string) => {
    const n = round2(toNum(amount));
    const cur = currency ? ` ${currency}` : "";
    return `${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}${cur}`;
};

const calcExcl = (qty: number, unitPrice: number) => round2(qty * unitPrice);
const calcIncl = (excl: number, vatPct: number) =>
    round2(excl * (1 + (vatPct || 0) / 100));

const buildTreeRows = (txns: Txn[], invoiceCurrency?: string): TreeGridRow[] => {
    const byId = new Map<string, Txn>();
    txns.forEach((t) => byId.set(String(t.id), t));

    const childrenMap = new Map<string, string[]>();
    txns.forEach((t) => {
        const pid = t.parent ? String(t.parent) : "";
        if (!pid) return;
        if (!childrenMap.has(pid)) childrenMap.set(pid, []);
        childrenMap.get(pid)!.push(String(t.id));
    });

    const isRoot = (t: Txn) => {
        const pid = t.parent ? String(t.parent) : "";
        return !pid || !byId.has(pid);
    };

    const sortByOrderIndex = (a: Txn, b: Txn) =>
        toNum(a.orderIndex) - toNum(b.orderIndex);

    const roots = txns.filter(isRoot).sort(sortByOrderIndex);
    const rows: TreeGridRow[] = [];

    const walk = (txn: Txn, parentId: string | null, depth: number) => {
        const id = String(txn.id);
        const childIds = (childrenMap.get(id) || []).slice();
        childIds.sort((a, b) => sortByOrderIndex(byId.get(a)!, byId.get(b)!));

        const qty = toNum(txn.itemQuantity);
        const unitPrice = toNum(
            txn.otherUnitPrice ?? txn.bookkeepingUnitPrice ?? txn.bookkeepingAmount
        );

        const vatPct = toNum(txn.VATValue);
        const currency =
            txn.otherCurrency || txn.bookkeepingCurrency || invoiceCurrency || "";

        const exclVAT = calcExcl(qty, unitPrice);
        const inclVAT = calcIncl(exclVAT, vatPct);

        const row: TreeGridRow = {
            id,
            parentId,
            depth,
            childrenIds: childIds,
            isExpanded: true,
            rowType: childIds.length > 0 ? "product" : "simple",
            sortOrder: toNum(txn.orderIndex),
            data: {
                // Figma columns
                name: txn.itemName || txn.itemDescription || txn.transactionsName || "",
                unitPrice,
                qty,
                vatPct,
                exclVAT,
                inclVAT,
                currency,

                // keep useful fields for mapping back
                description: txn.description || "",
                itemRefId: txn.itemRefId || "",
                accountName: txn.accountName || "",
            },
        };

        rows.push(row);

        childIds.forEach((cid) => {
            const child = byId.get(cid);
            if (child) walk(child, id, depth + 1);
        });
    };

    roots.forEach((r) => walk(r, null, 0));
    return rows;
};

const applyRowsToInvoiceTransactions = (invoice: any, rows: TreeGridRow[]) => {
    const list: Txn[] = Array.isArray(invoice?.TRANSACTION)
        ? [...invoice.TRANSACTION]
        : [];

    const byId = new Map<string, Txn>();
    list.forEach((t) => byId.set(String(t.id), t));

    rows.forEach((row) => {
        const txn = byId.get(String(row.id));
        if (!txn) return;

        // map back editable fields
        txn.itemName = row.data.name;
        txn.itemQuantity = String(toNum(row.data.qty));
        txn.otherUnitPrice = String(toNum(row.data.unitPrice));

        const excl = calcExcl(toNum(row.data.qty), toNum(row.data.unitPrice));
        txn.otherAmount = String(excl);

        // keep description if you want
        txn.description = row.data.description ?? txn.description;
    });

    return { ...invoice, TRANSACTION: list };
};

export default function InvoiceTransactionComponent({
    invoice,
    isEdit,
    onInvoiceChange,
}: InvoiceTransactionComponentProps) {
    const [rows, setRows] = useState<TreeGridRow[]>([]);

    useEffect(() => {
        const txns = Array.isArray(invoice?.TRANSACTION) ? invoice.TRANSACTION : [];
        setRows(buildTreeRows(txns, invoice?.currency));
    }, [invoice]);

    const columns: TreeGridColumn[] = useMemo(
        () => [
            {
                key: "name",
                header: "Name",
                width: '60%',
                editable: isEdit,
                editorType: "text",
                isTreeColumn: true,
                showFolderIcon: true,
                render: (value: any, row: TreeGridRow) => {
                    const name = String(value || "—");
                    return (
                        <div className={styles.nameCell}>
                            <div className={styles.nameTopRow}>
                                <span className={styles.nameText} title={name}>
                                    {name}
                                </span>
                            </div>
                            {/* optional subline like your screenshot */}
                            {row.data?.accountName ? (
                                <div className={styles.nameSubText} title={row.data.accountName}>
                                    {row.data.accountName}
                                </div>
                            ) : (
                                <div className={styles.nameSubText} />
                            )}
                        </div>
                    );
                },
            },
            {
                key: "unitPrice",
                header: "Unit Price",
                width: 140,
                editable: isEdit,
                editorType: "number",
                align: "right",
                numberFormat: { decimals: 2, thousandsSeparator: true },
                format: (value: any, row: TreeGridRow) =>
                    formatMoney(value, row.data?.currency),
                onCommit: (value: any, row: TreeGridRow) => {
                    row.data.unitPrice = toNum(value);
                    row.data.exclVAT = calcExcl(toNum(row.data.qty), toNum(row.data.unitPrice));
                    row.data.inclVAT = calcIncl(toNum(row.data.exclVAT), toNum(row.data.vatPct));
                },
            },
            {
                key: "qty",
                header: "Qty.",
                width: 80,
                editable: isEdit,
                editorType: "number",
                align: "right",
                numberFormat: { decimals: 2, thousandsSeparator: false },
                validate: (value: any) => {
                    const n = toNum(value);
                    if (!Number.isFinite(n) || n <= 0) return "Qty must be > 0";
                    return null;
                },
                onCommit: (value: any, row: TreeGridRow) => {
                    row.data.qty = toNum(value);
                    row.data.exclVAT = calcExcl(toNum(row.data.qty), toNum(row.data.unitPrice));
                    row.data.inclVAT = calcIncl(toNum(row.data.exclVAT), toNum(row.data.vatPct));
                },
            },
            {
                key: "vatPct",
                header: "VAT",
                width: 90,
                editable: false, // keep like figma (read-only)
                align: "right",
                format: (value: any) => `${toNum(value).toFixed(0)}%`,
            },
            {
                key: "exclVAT",
                header: "excl. VAT",
                width: 150,
                editable: false,
                align: "right",
                showTotal: true,
                totalLabel: "Total:",
                totalFormat: (total: number) => {
                    const cur = invoice?.currency ? ` ${invoice.currency}` : "";
                    return `${total.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}${cur}`;
                },
                format: (value: any, row: TreeGridRow) =>
                    formatMoney(value, row.data?.currency),
            },
            {
                key: "inclVAT",
                header: "incl. VAT",
                width: 150,
                editable: false,
                align: "right",
                showTotal: true,
                // keep label empty so only the excl. VAT column shows "Total:"
                totalLabel: "",
                totalFormat: (total: number) => {
                    const cur = invoice?.currency ? ` ${invoice.currency}` : "";
                    return `${total.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}${cur}`;
                },
                format: (value: any, row: TreeGridRow) =>
                    formatMoney(value, row.data?.currency),
            },
        ],
        [isEdit, invoice?.currency]
    );

    const handleRowUpdate = useCallback(
        async (row: TreeGridRow) => {
            // update local grid rows
            setRows((prev) => prev.map((r) => (r.id === row.id ? row : r)));

            // push back into invoice.TRANSACTION
            const nextRows = rows.map((r) => (r.id === row.id ? row : r));
            const nextInvoice = applyRowsToInvoiceTransactions(invoice, nextRows);
            onInvoiceChange(nextInvoice);

            ToastQueue.positive("Transaction updated", { timeout: 1500 });
        },
        [invoice, onInvoiceChange, rows]
    );

    const handleRowCreate = useCallback(() => {
        const newId = `new-${Date.now()}`;
        const currency = invoice?.currency || "";

        const newRow: TreeGridRow = {
            id: newId,
            parentId: null,
            depth: 0,
            childrenIds: [],
            isExpanded: false,
            rowType: "simple",
            sortOrder: 9999,
            data: {
                name: "New item",
                unitPrice: 0,
                qty: 1,
                vatPct: 0,
                exclVAT: 0,
                inclVAT: 0,
                currency,
                description: "",
                itemRefId: "",
                accountName: "",
            },
        };

        setRows((prev) => [...prev, newRow]);

        const newTxn: any = {
            id: newId, // temp
            orderIndex: "9999",
            itemName: "New item",
            itemQuantity: "1",
            otherUnitPrice: "0",
            otherAmount: "0",
            otherCurrency: currency,
            description: "",
        };

        onInvoiceChange({
            ...invoice,
            TRANSACTION: Array.isArray(invoice?.TRANSACTION)
                ? [...invoice.TRANSACTION, newTxn]
                : [newTxn],
        });
    }, [invoice, onInvoiceChange]);

    const handleRowDelete = useCallback(
        (rowId: string) => {
            // delete row + its direct children
            setRows((prev) =>
                prev.filter((r) => r.id !== rowId && r.parentId !== rowId)
            );

            const nextInvoice = { ...invoice };
            if (Array.isArray(nextInvoice?.TRANSACTION)) {
                nextInvoice.TRANSACTION = nextInvoice.TRANSACTION.filter(
                    (t: any) =>
                        String(t.id) !== String(rowId) &&
                        String(t.parent || "") !== String(rowId)
                );
            }
            onInvoiceChange(nextInvoice);

            ToastQueue.info("Item deleted", { timeout: 1500 });
        },
        [invoice, onInvoiceChange]
    );

    return (
        <div className={styles.wrapper}>
            <TreeGridComponent
                title={undefined}              // figma: no title bar
                showToolbar={false}            // figma: no top toolbar
                className={styles.figmaGrid}   // figma look
                gridId="invoice-transactions"
                columns={columns}
                data={rows}
                showSelection={false}          // figma: no checkboxes
                showSelectAll={false}
                enableDragDrop={true}          // figma: drag dots shown
                enableKeyboardNav={true}
                enableInlineEdit={isEdit}
                pageSize={50}
                indentPerLevel={24}
                maxDepth={6}
                showGridLines={false}          // figma: very subtle/no gridlines
                showHover={true}
                showInfoIcon={true}
                showDeleteIcon={true}
                showDeleteConfirmation={false} // ✅ makes delete immediate (fix “delete not working”)
                onRowUpdate={handleRowUpdate}
                onRowCreate={() => { }}         // not used; figma uses Add item button
                onRowDelete={handleRowDelete}
                onRowMove={() => { }}
                onInfoClick={(rowId, row) => {
                    console.log("Info click:", rowId, row);
                    ToastQueue.info("Info clicked (check console)", { timeout: 1200 });
                }}
                onDeleteClick={(rowId) => handleRowDelete(rowId)} // ✅ delete icon works
                emptyMessage="No items"
            />

            {/* Figma: Add item button below-left */}
            {/* <div className={styles.footerBar}>
                <Button
                    variant="cta"
                    UNSAFE_className={styles.addBtn}
                    onPress={handleRowCreate}
                    isDisabled={!isEdit}
                    aria-label={!isEdit ? "Switch to edit mode to add items" : "Add item"}
                >
                    <span className={styles.addIcon}>＋</span>
                    Add item
                </Button>
            </div> */}
        </div>
    );
}
